import { useEffect, useState } from "react";
import api from "@/lib/api";
import { publicOrigin } from "@/lib/publicUrl";
import { useAuth } from "@/context/AuthContext";
import { usePlatform } from "@/context/PlatformContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Coins, Sparkles, Loader2, Check, X, Copy, CreditCard, Zap, ArrowUpRight, ShieldCheck,
} from "lucide-react";
import { motion } from "framer-motion";

export default function Pricing() {
  const { user } = useAuth();
  const platform = usePlatform();
  const [tab, setTab] = useState("plans");
  const [interval, setInterval] = useState("month"); // month | year
  const [plans, setPlans] = useState([]);
  const [currentSub, setCurrentSub] = useState(null);
  const [packages, setPackages] = useState([]);
  const [coupon, setCoupon] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/subscriptions/plans").then((r) => setPlans(r.data));
    api.get("/subscriptions/current").then((r) => setCurrentSub(r.data)).catch(() => {});
    api.get("/packages").then((r) => setPackages(r.data));
  }, []);

  const validateCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.get(`/coupons/validate/${coupon.trim().toUpperCase()}`);
      setCouponInfo(data);
      toast.success(`Coupon applied: ${data.discount_percent}% off`);
    } catch (err) {
      setCouponInfo(null);
      toast.error(err?.response?.data?.detail || "Invalid coupon");
    }
  };

  const subscribe = async (planId) => {
    setBusy(`plan-${planId}-${interval}`);
    try {
      const { data } = await api.post("/subscriptions/checkout", {
        plan_id: planId,
        interval,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  const managePortal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/subscriptions/portal", {
        return_url: `${window.location.origin}/pricing`,
      });
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cannot open portal");
      setBusy(null);
    }
  };

  const buyPack = async (pkgId) => {
    setBusy(`pack-${pkgId}`);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: pkgId,
        origin_url: window.location.origin,
        coupon_code: couponInfo ? coupon.trim().toUpperCase() : null,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  const copyReferral = async () => {
    const url = `${publicOrigin()}/signup?ref=${user?.referral_code}`;
    await navigator.clipboard.writeText(url);
    toast.success("Referral link copied");
  };

  const activeTier = currentSub?.tier || "free";
  const yearlySavings = interval === "year" ? " · save 20%" : "";

  return (
    <div className="p-6 md:p-10 lg:p-14 max-w-[1400px]">
      <div className="mb-10">
        <div className="font-mono-tech text-[10px] text-amber-400 mb-3">MEDIAOTHERS · PRICING</div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-light leading-none">
          Pick your <span className="font-bold text-amber-400">plan.</span>
        </h1>
        <p className="text-zinc-400 mt-4 max-w-2xl">
          Monthly plans include credits and unlock features. Need a burst? Top up with one-time credit packs anytime.
        </p>
      </div>

      {/* Current subscription banner */}
      {currentSub?.has_subscription && (
        <div
          className="mb-8 flex flex-wrap items-center gap-4 border border-emerald-500/25 bg-emerald-500/[0.06] rounded-lg p-4"
          data-testid="current-sub-banner">
          <ShieldCheck className="w-5 h-5 text-emerald-400" strokeWidth={1.5} />
          <div className="flex-1 min-w-[200px]">
            <div className="font-mono-tech text-[10px] text-emerald-400">CURRENT PLAN</div>
            <div className="font-heading text-lg font-semibold tracking-tight">
              {currentSub.plan?.name} · {currentSub.interval === "year" ? "Annual" : "Monthly"}
            </div>
            {currentSub.period_end && (
              <div className="text-xs text-zinc-400 mt-0.5">
                {currentSub.cancel_at_period_end ? "Cancels" : "Renews"} on {new Date(currentSub.period_end).toLocaleDateString()}
                {currentSub.status && currentSub.status !== "active" && (
                  <span className="ml-2 text-amber-400">· {currentSub.status}</span>
                )}
              </div>
            )}
          </div>
          <Button onClick={managePortal} disabled={busy === "portal"} data-testid="open-portal"
            className="bg-white/10 hover:bg-white/15 border border-white/15 text-white">
            {busy === "portal" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowUpRight className="w-4 h-4 mr-2" strokeWidth={1.5} />}
            Manage subscription
          </Button>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="space-y-8">
        <TabsList className="bg-[#121214] border border-white/5 p-1">
          <TabsTrigger value="plans" data-testid="tab-plans"
            className="data-[state=active]:bg-amber-500 data-[state=active]:text-black gap-2 px-5">
            <Zap className="w-3.5 h-3.5" strokeWidth={1.5} /> Subscription plans
          </TabsTrigger>
          <TabsTrigger value="credits" data-testid="tab-credits"
            className="data-[state=active]:bg-amber-500 data-[state=active]:text-black gap-2 px-5">
            <Coins className="w-3.5 h-3.5" strokeWidth={1.5} /> Credit packs
          </TabsTrigger>
        </TabsList>

        {/* -------------------- SUBSCRIPTION PLANS -------------------- */}
        <TabsContent value="plans" className="space-y-6">
          {/* Interval toggle */}
          <div className="flex items-center gap-3">
            <div className="inline-flex bg-[#121214] border border-white/5 rounded-full p-1" data-testid="interval-toggle">
              <button
                onClick={() => setInterval("month")}
                data-testid="interval-monthly"
                className={`px-4 py-1.5 text-sm rounded-full font-medium transition-colors ${
                  interval === "month" ? "bg-amber-500 text-black" : "text-zinc-400 hover:text-white"
                }`}>
                Monthly
              </button>
              <button
                onClick={() => setInterval("year")}
                data-testid="interval-yearly"
                className={`px-4 py-1.5 text-sm rounded-full font-medium transition-colors ${
                  interval === "year" ? "bg-amber-500 text-black" : "text-zinc-400 hover:text-white"
                }`}>
                Yearly
              </button>
            </div>
            <span className="text-xs text-emerald-400 font-mono-tech">
              {interval === "year" ? "SAVING · 20 · %" : "SAVE · 20 · % · YEARLY"}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="plans-grid">
            {plans.map((p, i) => {
              const priceObj = p.prices?.find((x) => x.interval === interval);
              const isFree = p.id === "free";
              const isEnterprise = p.id === "enterprise";
              const isActive = activeTier === p.id;
              const isCurrentInterval = isActive && currentSub?.interval === interval;
              const price = priceObj ? priceObj.amount_cents / 100 : 0;
              const monthlyEq = interval === "year" && priceObj ? price / 12 : price;

              return (
                <motion.div key={p.id}
                  initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className={`relative rounded-lg border p-5 flex flex-col ${
                    p.highlight
                      ? "border-amber-500 bg-gradient-to-br from-amber-500/10 to-transparent"
                      : isActive
                      ? "border-emerald-500/40 bg-emerald-500/[0.04]"
                      : "border-white/5 bg-[#121214]"
                  }`}
                  data-testid={`plan-${p.id}`}>
                  {p.highlight && (
                    <div className="absolute -top-3 left-5 font-mono-tech text-[9px] px-2 py-1 bg-amber-500 text-black rounded">
                      MOST POPULAR
                    </div>
                  )}
                  {p.trial_days > 0 && !isActive && (
                    <div className="absolute -top-3 right-5 font-mono-tech text-[9px] px-2 py-1 bg-emerald-500 text-black rounded"
                      data-testid={`trial-badge-${p.id}`}>
                      {p.trial_days} DAYS FREE
                    </div>
                  )}
                  {isActive && (
                    <div className="absolute -top-3 right-5 font-mono-tech text-[9px] px-2 py-1 bg-emerald-500 text-black rounded">
                      CURRENT
                    </div>
                  )}
                  <div className="font-heading text-xl font-bold tracking-tight">{p.name}</div>
                  <div className="text-[11px] text-zinc-500 font-mono-tech mt-1 uppercase tracking-wider">
                    {p.tagline}
                  </div>

                  <div className="mt-5">
                    {isFree ? (
                      <div className="font-heading text-4xl font-bold tracking-tighter">€0</div>
                    ) : isEnterprise ? (
                      <div className="font-heading text-3xl font-bold tracking-tighter">Let&apos;s talk</div>
                    ) : (
                      <>
                        <div className="flex items-baseline gap-1.5">
                          <span className="font-heading text-4xl font-bold tracking-tighter text-amber-400">
                            €{monthlyEq.toFixed(0)}
                          </span>
                          <span className="text-xs text-zinc-500">/mo</span>
                        </div>
                        {interval === "year" && (
                          <div className="text-[11px] text-zinc-500 mt-1">
                            €{price.toFixed(0)} billed annually{yearlySavings}
                          </div>
                        )}
                        {p.trial_days > 0 && !isActive && (
                          <div className="text-[11px] text-emerald-400 mt-1 font-medium">
                            Free for {p.trial_days} days · then €{monthlyEq.toFixed(0)}/mo
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <div className="mt-4 space-y-1.5 text-xs text-zinc-300 flex-1">
                    {p.features?.map((f, idx) => {
                      // Render **bold** markdown-lite
                      const parts = f.split(/(\*\*[^*]+\*\*)/g);
                      return (
                        <div key={idx} className="flex items-start gap-1.5">
                          <Check className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" strokeWidth={2} />
                          <span>
                            {parts.map((part, j) =>
                              part.startsWith("**") && part.endsWith("**")
                                ? <b key={j} className="text-emerald-400">{part.slice(2, -2)}</b>
                                : <span key={j}>{part}</span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {isFree ? (
                    <Button
                      disabled
                      data-testid={`plan-${p.id}-cta`}
                      className="w-full h-10 mt-5 bg-white/5 border border-white/10 text-zinc-400 cursor-default">
                      Included by default
                    </Button>
                  ) : isEnterprise ? (
                    <a href={`mailto:${platform.sales_email}`} data-testid={`plan-${p.id}-cta`}>
                      <Button className="w-full h-10 mt-5 bg-white/10 hover:bg-white/15 border border-white/10">
                        Contact sales
                      </Button>
                    </a>
                  ) : isCurrentInterval ? (
                    <Button onClick={managePortal} disabled={busy === "portal"} data-testid={`plan-${p.id}-cta`}
                      className="w-full h-10 mt-5 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-300">
                      Manage
                    </Button>
                  ) : (
                    <Button
                      onClick={() => subscribe(p.id)}
                      disabled={busy?.startsWith(`plan-${p.id}`)}
                      data-testid={`plan-${p.id}-cta`}
                      className={`w-full h-10 mt-5 font-semibold ${
                        p.highlight
                          ? "bg-amber-500 hover:bg-amber-600 text-black"
                          : "bg-white/10 hover:bg-white/15 border border-white/10 text-white"
                      }`}>
                      {busy?.startsWith(`plan-${p.id}`) ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <CreditCard className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      )}
                      {isActive ? "Switch interval" : p.trial_days > 0 ? `Start ${p.trial_days}-day trial` : "Choose plan"}
                    </Button>
                  )}
                </motion.div>
              );
            })}
          </div>

          <p className="text-xs text-zinc-500 text-center pt-2">
            All prices in EUR. VAT calculated at checkout. Cancel anytime from the customer portal.
          </p>
        </TabsContent>

        {/* -------------------- CREDIT PACKS -------------------- */}
        <TabsContent value="credits" className="space-y-6">
          <p className="text-sm text-zinc-400">
            One-time top-ups on top of your plan (or as a pure pay-as-you-go option). Credits never expire.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="packages-grid">
            {packages.map((p, i) => {
              const totalCredits = p.credits + (p.bonus_credits || 0);
              const price = couponInfo
                ? Math.round(p.price_cents * (1 - couponInfo.discount_percent / 100))
                : p.price_cents;
              return (
                <motion.div key={p.id}
                  initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className={`relative rounded-lg border p-6 flex flex-col ${
                    p.highlight ? "border-amber-500 bg-gradient-to-br from-amber-500/10 to-transparent"
                      : "border-white/5 bg-[#121214]"
                  }`}
                  data-testid={`package-${p.id}`}>
                  {p.highlight && (
                    <div className="absolute -top-3 left-6 font-mono-tech text-[9px] px-2 py-1 bg-amber-500 text-black rounded">
                      MOST POPULAR
                    </div>
                  )}
                  <div className="font-heading text-2xl font-bold tracking-tight">{p.name}</div>
                  <div className="flex items-baseline gap-2 mt-3">
                    <div className="font-heading text-5xl font-bold tracking-tighter text-amber-400">
                      {totalCredits.toLocaleString()}
                    </div>
                    <div className="text-sm text-zinc-400">credits</div>
                  </div>
                  {p.bonus_credits > 0 && (
                    <div className="text-xs text-emerald-400 font-medium mt-1">
                      +{p.bonus_credits} bonus credits included
                    </div>
                  )}
                  <div className="mt-6 flex items-baseline gap-2">
                    {couponInfo && (
                      <span className="text-zinc-500 line-through text-sm">€{(p.price_cents / 100).toFixed(2)}</span>
                    )}
                    <span className="font-heading text-3xl font-bold tracking-tight">€{(price / 100).toFixed(2)}</span>
                  </div>
                  <div className="mt-4 space-y-1.5 text-xs text-zinc-400 flex-1">
                    <div className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-400" strokeWidth={2} /> Never expires</div>
                    <div className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-400" strokeWidth={2} /> Stack on any plan</div>
                  </div>
                  <Button onClick={() => buyPack(p.id)} disabled={busy === `pack-${p.id}`}
                    data-testid={`buy-${p.id}`}
                    className={`w-full h-11 mt-6 font-semibold ${
                      p.highlight ? "bg-amber-500 hover:bg-amber-600 text-black" : "bg-white/10 hover:bg-white/15 text-white border border-white/10"
                    }`}>
                    {busy === `pack-${p.id}` ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Coins className="w-4 h-4 mr-2" strokeWidth={1.5} />}
                    {busy === `pack-${p.id}` ? "Loading…" : "Buy credits"}
                  </Button>
                </motion.div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
            {/* Coupon */}
            <div className="bg-[#121214] border border-white/5 rounded-lg p-5">
              <div className="font-mono-tech text-[10px] text-zinc-500 mb-3">COUPON · CODE</div>
              <div className="flex gap-2">
                <Input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                  placeholder="SUMMER30"
                  data-testid="coupon-input"
                  className="bg-[#0b0b0d] border-white/10 font-mono-tech uppercase" />
                <Button onClick={validateCoupon} data-testid="coupon-apply"
                  className="bg-white/10 hover:bg-white/15 border border-white/10">Apply</Button>
              </div>
              {couponInfo && (
                <div className="mt-3 flex items-center gap-2 text-emerald-400 text-sm">
                  <Check className="w-4 h-4" strokeWidth={1.5} />
                  {couponInfo.discount_percent}% off applied
                  <button onClick={() => { setCouponInfo(null); setCoupon(""); }} className="ml-auto text-zinc-500 hover:text-red-400">
                    <X className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              )}
            </div>

            {/* Referral */}
            <div className="bg-gradient-to-br from-amber-500/10 to-transparent border border-amber-500/20 rounded-lg p-5">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-amber-400" strokeWidth={1.5} />
                <div className="font-mono-tech text-[10px] text-amber-400">REFER · A · FRIEND</div>
              </div>
              <div className="text-sm text-zinc-300 mb-3">
                Share your link — both of you get bonus credits when they sign up.
              </div>
              <div className="flex gap-2">
                <Input readOnly value={`${publicOrigin()}/signup?ref=${user?.referral_code || ""}`}
                  className="bg-[#0b0b0d] border-white/10 font-mono-tech text-xs" />
                <Button onClick={copyReferral} data-testid="copy-referral" className="bg-amber-500 hover:bg-amber-600 text-black">
                  <Copy className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
